import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, LoadingSpinner } from '../atoms';
import { ButtonWithDropDownMenu } from '../molecules';
import { useErrorHandlerContext, usePropertyDetailContext, useUserLanguage } from '@/customHooks';
import { useInventoryFormModalContext } from '@/customHooks/useInventoryFormModalContext';
import type { InventoryInspectionOutput, InventoryInspectionPdfOutput } from '@/custom-types';
import textsData from '@/textData/PropertyDetailTexts.json';
import { GET_INVENTORIES } from '@/graphql/queries/getInventories';
import { GENERATE_INVENTORY_INSPECTION_PDF } from '@/graphql/mutations/inventoryInspections';
import { downloadFileFromUrl } from '@/helpers';

type InventoriesQueryResult = {
  inventoryInspections: InventoryInspectionOutput[];
};

export const PropertyDetailInventoriesList = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.inventoriesList : textsData.en.inventoriesList;

  const { property } = usePropertyDetailContext();
  const { open: errorHandle } = useErrorHandlerContext();
  const { openCreateModal, openEditModal, openDetailsModal } = useInventoryFormModalContext();

  const [generatePdf, { loading: generatePdfLoading }] = useMutation<{
    generateInventoryInspectionPdf: InventoryInspectionPdfOutput;
  }, {
    propertyId: string;
    inspectionId: string;
  }>(GENERATE_INVENTORY_INSPECTION_PDF);

  const { data, loading, error, refetch } = useQuery<InventoriesQueryResult>(GET_INVENTORIES, {
    variables: {
      propertyId: property?.id ?? '',
    },
    skip: !property?.id,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (error) {
      errorHandle(error);
    }
  }, [error, errorHandle]);

  const prevPropertyRef = useRef(property);

  useEffect(() => {
    if (property?.id) {
      const wasUndefined = prevPropertyRef.current === undefined;
      const idChanged = prevPropertyRef.current?.id !== property.id;
      const referenceChanged = prevPropertyRef.current !== property;

      if (wasUndefined || idChanged || referenceChanged) {
        prevPropertyRef.current = property;
        const timeoutId = setTimeout(() => {
          refetch({ propertyId: property.id });
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
    prevPropertyRef.current = property;
  }, [property, refetch]);

  const inventories = useMemo(() => data?.inventoryInspections ?? [], [data]);

  const handleNewInventory = () => {
    if (!property?.id) return;
    openCreateModal(property.id);
  };

  const totalInventories = inventories.length;
  const initialInventories = inventories.filter((inv) => inv.type === 'INITIAL').length;
  const finalInventories = inventories.filter((inv) => inv.type === 'FINAL').length;

  const handleEditInventory = (inventory: InventoryInspectionOutput) => {
    if (!property?.id) return;
    openEditModal(property.id, inventory);
  };

  const handleDetailsInventory = (inventory: InventoryInspectionOutput) => {
    if (!property?.id) return;
    openDetailsModal(property.id, inventory);
  };

  const handleDownloadPdf = async (inventory: InventoryInspectionOutput) => {
    try {
      if (!property?.id) return;
      if (generatePdfLoading) return;

      const { data } = await generatePdf({
        variables: { propertyId: property.id, inspectionId: inventory.id },
      });

      const payload = data?.generateInventoryInspectionPdf;
      if (!payload?.url) return;

      await downloadFileFromUrl(payload.url, payload.fileName ?? 'inspection.pdf');
    } catch (e) {
      errorHandle(e);
    }
  };

  if (!property?.id) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center">
        <LoadingSpinner text />
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="flex items-center gap-4">
        <h2 className="text-xl font-semibold">{texts.title}</h2>
        <div className="ml-auto">
          <Button onClick={handleNewInventory}>+ {texts.createButton}</Button>
        </div>
      </div>

      <div className="mt-4 mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-md">
          <span className="mt-1 text-3xl font-bold">{totalInventories}</span>
          <span className="mt-2 text-sm text-slate-700">{texts.stats.saved}</span>
        </div>
        <div className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-md">
          <span className="mt-1 text-3xl font-bold">{initialInventories}</span>
          <span className="mt-2 text-sm text-slate-700">{texts.stats.initial}</span>
        </div>
        <div className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-md">
          <span className="mt-1 text-3xl font-bold">{finalInventories}</span>
          <span className="mt-2 text-sm text-slate-700">{texts.stats.final}</span>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white p-6 shadow-md">
        {inventories.length === 0 ? (
          <div className="flex min-h-[180px] flex-col items-center justify-center text-center text-slate-700">
            <p className="font-semibold">{texts.noData}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-[1.5fr_1fr_0.8fr_100px] border-b border-gray-200 py-3 text-sm font-semibold">
              <span>{texts.tableHeader.description}</span>
              <span>{texts.tableHeader.itemsCount}</span>
              <span>{texts.tableHeader.type}</span>
              <span />
            </div>
            {inventories.map((inventory) => (
              <div
                key={inventory.id}
                className="grid grid-cols-[1.5fr_1fr_0.8fr_100px] items-center border-b border-gray-200 py-3 text-sm"
              >
                <span>{inventory.generalNotes || texts.tableHeader.untitledInventory}</span>
                <span>
                  {inventory.sections.reduce(
                    (acc: number, section) => acc + section.items.length,
                    0,
                  )}
                </span>
                <span>
                  <span
                    className={`inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-semibold text-white ${
                      inventory.type === 'INITIAL'
                        ? 'bg-[var(--color-primary-2)]'
                        : inventory.type === 'FINAL'
                        ? 'bg-[var(--color-primary-1)]'
                        : 'bg-slate-700'
                    }`}
                  >
                    {inventory.type === 'INITIAL'
                      ? texts.typeLabels.initial
                      : texts.typeLabels.final}
                  </span>
                </span>
                <span>
                  <ButtonWithDropDownMenu
                    options={[
                      { item: texts.dropdownMenu.details, action: () => handleDetailsInventory(inventory) },
                      { item: texts.dropdownMenu.edit, action: () => handleEditInventory(inventory) },
                      { item: texts.dropdownMenu.downloadPdf, action: () => handleDownloadPdf(inventory) },
                    ]}
                    position="left"
                  />
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

